import { store } from "../store";
import { setAlert, removeAlert } from "../reducers/userConfigSlice";
import { translateMessage } from "@delibrandev/utils";
import { apiRest } from "./constants";
import { decodeToken, encodeToken } from "./utils";
import axios from "axios";

// DEFAULT AXIOS CONFIGURATION
axios.defaults.withCredentials = true;
axios.defaults.baseURL = apiRest;

// Axios errors can contain request bodies, tokens and credentials.
const logRequestError = (operation, error) => {
  const status = error?.response?.status ?? error?.status;
  console.error(`Error de petición (${operation})`, {
    status: Number.isInteger(status) ? status : null,
  });
};

// A rejected HTTP request can never be interpreted as a successful save.
// Keep the API envelope for existing forms and also surface it above dialogs.
const requestFailure = (error, path, method = "post") => {
  const body = error?.response?.data;
  const message = typeof body?.message === "string" && body.message.trim()
    ? body.message
    : "No se pudo completar la acción. Comprueba los datos o la conexión y vuelve a intentarlo.";
  const translated = translateMessage(message);
  store.dispatch(setAlert({
    requestKey: `${method}:${path}`,
    type: "error",
    title: "No se pudo completar la acción",
    message: translated?.message || message,
  }));
  return { ...(body && typeof body === "object" ? body : {}), error: true, message };
};

const clearRequestFailure = (path, method = "post") => {
  const alerts = store.getState().aplicationConfig.userConfig.alerts;
  alerts.filter(alert => alert.requestKey === `${method}:${path}`)
    .forEach(alert => store.dispatch(removeAlert(alert.id)));
};

// Métodos HTTP a funciones de axios
const methods = {
  get: axios.get,
  post: axios.post,
  patch: axios.patch,
  delete: axios.delete,
  put: axios.put,
};

// Función recursiva para manejar objetos anidados
export function appendFormData(formData, key, value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    // Si es un objeto, convertirlo a JSON
    formData.append(key, JSON.stringify(value));
  } else if (Array.isArray(value)) {
    // Si es un arreglo, recorrer sus elementos
    value.forEach((item, index) => {
      formData.append(`${key}[${index}]`, JSON.stringify(item));
    });
  } else if (value !== null && value !== undefined) {
    // Para valores primitivos o no nulos/indefinidos
    formData.append(key, value);
  }
}

export const apiBasic = async (data, path, method = "post") => {
  try {
    let payload;
    let headers = {};

    if (data?.files) {
      headers["Content-Type"] = "multipart/form-data";
      const dataForm = new FormData();

      // Recorrer el objeto principal
      for (const [key, value] of Object.entries(data)) {
        appendFormData(dataForm, key, value);
      }

      // Recorrer imágenes
      data.files.length > 0 ? data.files.forEach((image) => dataForm.append("files", image)) : dataForm.append("files", data.files);

      payload = dataForm;
    } else {
      payload = data;
    }

    // If delete metod
    if (method === "delete") payload = { data: data };

    // Selección del método basado en la entrada o fallback a POST
    const httpMethod = methods[method] || axios.post;
    // Ejecución de la solicitud
    const response = await httpMethod(apiRest + path, payload);
    if (response.data?.error !== true) clearRequestFailure(path, method);
    return response.data;
  } catch (error) {
    logRequestError("apiBasic", error);
    return requestFailure(error, path, method);
    //throw error; // Rethrow para manejarlo en un nivel superior si es necesario
  }
};

export const apiToken = async (data, path, method = "post") => {
  try {
    const token = encodeToken(data);
    let payload;
    let headers = {};

    if (data?.files) {
      headers["Content-Type"] = "multipart/form-data";
      const dataForm = new FormData();

      // Recorrer el objeto principal
      for (const [key, value] of Object.entries(data)) {
        appendFormData(dataForm, key, value);
      }

      // Recorrer imágenes
      data.files.forEach((image) => dataForm.append("files", image));

      payload = dataForm;
    } else {
      payload = { token };
    }

    // If delete metod
    if (method === "delete") payload = { data: data };

    const httpMethod = methods[method] || axios.post;
    const response = await httpMethod(apiRest + path, payload, { headers });
    if (response.data?.error !== true) clearRequestFailure(path, method);
    // Decode
    return { ...response.data, data: decodeToken(response.data) };
  } catch (error) {
    logRequestError("apiToken", error);
    return requestFailure(error, path, method);
  }
};

export const apiTokenNotDecode = async (data, path) => {
  console.log("apiTokenNotDecode");
  try {
    const token = encodeToken(data);

    // 👇 ¡clave!: withCredentials para aceptar Set-Cookie y enviar Cookie luego
    const response = await axios.post(apiRest + path, { token }, { withCredentials: true, headers: { "Content-Type": "application/json" } });

    if (response.data?.error !== true) clearRequestFailure(path, "post");
    return response; // mantengo tu contrato actual
  } catch (error) {
    logRequestError("apiToken", error);
    return requestFailure(error, path, "post");
  }
};

export const apiGet = async (path, params = "") => {
  try {
    const response = await axios.get(path, {
      params: params,
    });
    if (response.data?.error !== true) clearRequestFailure(path, "get");
    return response.data;
  } catch (error) {
    logRequestError("apiGet", error);
    return requestFailure(error, path, "get");
  }
};

export const logout = async () => {
  const baseApi = apiRest.replace(/^(https?:\/\/[^/]+).*$/, "$1");
  try {
    let payload;
    // Selección del método
    const httpMethod = methods["delete"];
    // Ejecución de la solicitud
    const response = await httpMethod(baseApi + "/logout", payload);
    return response.data;
  } catch (error) {
    logRequestError("logout", error);
    return requestFailure(error, "session");
  }
};

export const getCorporationInfo = async () => {
  const baseApi = apiRest.replace(/^(https?:\/\/[^/]+).*$/, "$1");
  try {
    let payload;
    // Selección del método
    const httpMethod = methods["get"];
    // Ejecución de la solicitud
    const response = await httpMethod(baseApi + "/get-corporation-info", payload);
    return response.data;
  } catch (error) {
    logRequestError("getCorporationInfo", error);
    return requestFailure(error, "session");
  }
};
